import { useEffect, useState } from 'react';
import { IconAlert, IconCheck, IconClose } from './Icon';

export type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastEventDetail {
  message: string;
  kind: ToastKind;
}

const TOAST_EVENT = 'app:toast';
const DEFAULT_TTL_MS = 4_000;

// Public helper. Anywhere in the app can call `toast('Saved', 'success')`
// and the ToastHost mounted in App will render it. Decoupling via custom
// events means callers don't need to thread props or a context provider
// through every component tree.
export function toast(message: string, kind: ToastKind = 'info'): void {
  if (typeof window === 'undefined') return;
  const detail: ToastEventDetail = { message, kind };
  window.dispatchEvent(new CustomEvent<ToastEventDetail>(TOAST_EVENT, { detail }));
}

let nextId = 1;

export function ToastHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    function onToast(e: Event) {
      const ce = e as CustomEvent<ToastEventDetail>;
      if (!ce.detail) return;
      const id = nextId++;
      const t: Toast = { id, message: ce.detail.message, kind: ce.detail.kind };
      setToasts((prev) => [...prev, t]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== id));
      }, DEFAULT_TTL_MS);
    }
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, []);

  function dismiss(id: number) {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed top-4 right-4 z-[60] flex flex-col gap-2 max-w-sm pointer-events-none"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto rounded-lg shadow-xl p-3 flex items-start gap-3 animate-fade-up hairline-strong ${
            t.kind === 'success'
              ? 'bg-emerald-500/10 text-emerald-100'
              : t.kind === 'error'
                ? 'bg-rose-500/10 text-rose-100'
                : 'bg-zinc-900 text-zinc-100'
          }`}
        >
          <span
            className={`shrink-0 mt-0.5 ${
              t.kind === 'success'
                ? 'text-emerald-400'
                : t.kind === 'error'
                  ? 'text-rose-400'
                  : 'text-zinc-400'
            }`}
          >
            {t.kind === 'success' ? (
              <IconCheck size={14} />
            ) : t.kind === 'error' ? (
              <IconAlert size={14} />
            ) : (
              <IconCheck size={14} />
            )}
          </span>
          <p className="flex-1 min-w-0 text-sm leading-snug">{t.message}</p>
          <button
            onClick={() => dismiss(t.id)}
            aria-label="Dismiss"
            className="shrink-0 text-zinc-400 hover:text-zinc-100 transition"
          >
            <IconClose size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}
